import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography, TextField } from '@mui/material';
import moment from 'moment';
import {
  PageContainer,
  ContentContainer,
  InfoBox,
  ButtonContainer,
} from './FeedbackPage.styles';
import RatingSection from './components/RatingSection';
import FutureCollaboration from './components/FutureCollaboration';
import TopBarRedesigned from 'components/TopBarRedesigned/TopBarRedesigned';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import { exitReact } from 'utils';
import { JobProps, HygienistProps } from 'utils/types';
import ApiError from 'api/ApiError';
import api from 'api';
import { RATINGS } from 'utils/constants';
import PositiveFeedbackModal from './components/PositiveFeedbackModal';
import NegativeFeedbackModal from './components/NegativeFeedbackModal';
import PositiveSuccessModal from './components/PositiveSuccessModal';
import NegativeSuccessModal from './components/NegativeSuccessModal';
import useActionApi from 'hooks/useActionApi';
import FixedLoader from 'components/FixedLoader';
import FixedError from 'components/FixedError/FixedError';

interface FeedbackPageProps {
  onClose: () => void;
  talentName: string;
  hourlyRate: number;
  onSubmit: (feedback: FeedbackData) => void;
  surveyJobs?: { job: JobProps; hygienist: HygienistProps; locationId: string }[];
  currentSurveyIndex?: number;
  fromJobCompleted?: boolean;
}

interface RatingData {
  rating: number | null;
  comment: string;
}

interface FeedbackData {
  clinical: RatingData;
  professional: RatingData;
  communication: RatingData;
  time: RatingData;
  value: RatingData;
  hygienistRating: any;
  additionalFeedback: string;
  surveyDismissed?: string;
  feedbackSubmitted?: number;
}

interface PostponeSurveyItem {
  jobId: number;
  postponeUntil: string;
}

type RatingFieldKey = 'clinical' | 'professional' | 'communication' | 'time' | 'value';
type HygienistRatingValue = '' | string | null; 

const FeedbackPage: React.FC<FeedbackPageProps> = () => {
  const history = useHistory();
  const { job, hygienist, locationId, surveyJobs, currentSurveyIndex, fromJobCompleted } = history.location.state as { 
    job: JobProps;
    hygienist: HygienistProps;
    locationId: string;
    surveyJobs?: { job: JobProps; hygienist: HygienistProps; locationId: string }[];
    currentSurveyIndex?: number;
    fromJobCompleted?: boolean;
  };

  const ratingSections = [
    {
      title: "Clinical Skills & Knowledge",
      description: `How would you rate ${hygienist.firstName}'s clinical competence and dental knowledge?`,
      type: "clinical" as RatingFieldKey,
    },
    {
      title: "Professionalism",
      description: `How professional was ${hygienist.firstName}'s appearance, demeanor, and overall conduct?`,
      type: "professional" as RatingFieldKey,
    },
    {
      title: "Communication & Patient Care",
      description: `How well did ${hygienist.firstName} communicate with patients and staff, and provide patient care?`,
      type: "communication" as RatingFieldKey,
    },
    {
      title: "Time Management & Punctuality",
      description: `Was ${hygienist.firstName} punctual and efficient in managing their time throughout the day?`,
      type: "time" as RatingFieldKey,
    },
    {
      title: "Rate Satisfaction",
      description: `${hygienist.firstName}'s hourly rate was $${job.hourlyRate}/hr - did you receive good value for the hourly rate?`,
      type: "value" as RatingFieldKey,
    }
  ];

  const [feedback, setFeedback] = useState<FeedbackData>({
    clinical: { rating: null, comment: '' },
    professional: { rating: null, comment: '' },
    communication: { rating: null, comment: '' },
    time: { rating: null, comment: '' },
    value: { rating: null, comment: '' },
    hygienistRating: '',
    additionalFeedback: '',
    feedbackSubmitted: 0
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showNegativeSuccessModal, setShowNegativeSuccessModal] = useState(false);
  const [showPositiveModal, setShowPositiveModal] = useState(false);
  const [showNegativeModal, setShowNegativeModal] = useState(false);
  const [selectedPositiveFeedback, setSelectedPositiveFeedback] = useState(`"Thanks so much, ${hygienist.firstName}!"`);
  const [negativeFeedback, setNegativeFeedback] = useState("");
  const [contactRequested, setContactRequested] = useState(false);
  const [contactName, setContactName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<ApiError | null>(null);

  const [saveFeedbackLoading, saveFeedbackError, submitFeedback] = useActionApi({
    apiMethod: api.saveFeedback,
    onSuccess: (_response: unknown) => {
      setSubmitting(false);
      finalizeFeedback();
      
      if (feedback.surveyDismissed === "1") {
        handleDismissNavigation();
        return;
      }
      
      if (feedback.hygienistRating === RATINGS.VERY_HAPPY.toString()) {
        setShowPositiveModal(true);
      } else if (feedback.hygienistRating === RATINGS.UNHAPPY.toString()) {
        setShowNegativeModal(true);
      } else {
        handleRedirect();
      }
    },
    onError: (error) => {
      setSubmitting(false);
      setApiError(error);
    }
  });

  const [positiveFeedbackLoading, positiveFeedbackError, submitPositiveFeedback] = useActionApi({
    apiMethod: api.positiveFeedback,
    onSuccess: (_response: unknown) => {
      setShowSuccessModal(true);
    },
    onError: (error) => {
      setApiError(error);
    }
  });

  const [negativeFeedbackLoading, negativeFeedbackError, submitNegativeFeedback] = useActionApi({
    apiMethod: api.negativeFeedback,
    onSuccess: (_response: unknown) => {
      setShowNegativeSuccessModal(true);
    },
    onError: (error) => {
      setApiError(error);
    }
  });

  const hasInput = useCallback(() => {
    return (
      feedback.clinical.rating !== null ||
      feedback.professional.rating !== null ||
      feedback.communication.rating !== null ||
      feedback.time.rating !== null ||
      feedback.value.rating !== null ||
      feedback.hygienistRating !== '' ||
      feedback.clinical.comment !== '' ||
      feedback.professional.comment !== '' ||
      feedback.communication.comment !== '' ||
      feedback.time.comment !== '' ||
      feedback.value.comment !== '' ||
      feedback.additionalFeedback !== ''
    );
  }, [feedback]);

  const handleBack = () => {
    try {
      let postponedSurveys: PostponeSurveyItem[] = [];
      const storedSurveys = localStorage.getItem('postponeSurvey');
      if (storedSurveys) {
        postponedSurveys = JSON.parse(storedSurveys) as PostponeSurveyItem[];
      }
      postponedSurveys = postponedSurveys.filter(item =>
        moment(item.postponeUntil).isAfter(moment())
      );
      postponedSurveys.push({
        jobId: job.id,
        postponeUntil: moment().add(1, 'day').format()
      });
      localStorage.setItem('postponeSurvey', JSON.stringify(postponedSurveys));

      if (surveyJobs && currentSurveyIndex !== undefined && currentSurveyIndex + 1 < surveyJobs.length) {
        const nextSurvey = surveyJobs[currentSurveyIndex + 1];
        history.replace('feedbackPage', {
          ...nextSurvey,
          surveyJobs,
          currentSurveyIndex: currentSurveyIndex + 1
        });

        setFeedback({
          clinical: { rating: null, comment: '' },
          professional: { rating: null, comment: '' },
          communication: { rating: null, comment: '' },
          time: { rating: null, comment: '' },
          value: { rating: null, comment: '' },
          hygienistRating: '',
          additionalFeedback: '',
          feedbackSubmitted: 0
        });
        setSelectedPositiveFeedback(`"Thanks so much, ${nextSurvey.hygienist.firstName}!"`);
        setNegativeFeedback("");
        setContactRequested(false);
        setContactName("");
      } else {
        history.push('loading');
        exitReact('home');
      }
    } catch (error) {
      console.error('Error handling postpone survey:', error);
      history.push('loading');
      exitReact('home');
    }
  };

  const handleDismissWithoutFeedback = () => {
    try {
      if (saveFeedbackLoading || submitting) return;
      
      setSubmitting(true);
      setApiError(null);
      
      const feedbackData = {
        jobId: job.id,
        hygienistId: hygienist.id,
        locationId: locationId,
        clinical: { rating: null, comment: '' },
        professional: { rating: null, comment: '' },
        communication: { rating: null, comment: '' },
        time: { rating: null, comment: '' },
        value: { rating: null, comment: '' },
        hygienistRating: null as HygienistRatingValue,
        additionalFeedback: 'Survey dismissed without feedback',
        surveyDismissed: "1",
        feedbackSubmitted: 0
      };

      setFeedback(prev => ({
        ...prev,
        surveyDismissed: "1",
        feedbackSubmitted: 0
      }));
      
      submitFeedback({ data: feedbackData });
    } catch (error) {
      console.error('Error in dismiss handler:', error);
      setSubmitting(false);
      setApiError(new ApiError(500, 'An unexpected error occurred. Please try again.'));
    }
  };
  const handleDismissNavigation = () => {
    try {
      if (fromJobCompleted) {
        history.push('loading');
        exitReact('home');
        return;
      }

      if (surveyJobs && currentSurveyIndex !== undefined) {
        const updatedSurveyJobs = surveyJobs.filter(survey => survey.job.id !== job.id);
        if (updatedSurveyJobs.length === 0) {
          history.push('loading');
          exitReact('home');
          return;
        }        
        history.replace('feedbackPage', {
          ...updatedSurveyJobs[0],
          surveyJobs: updatedSurveyJobs,
          currentSurveyIndex: 0
        });
      } else {
        history.push('loading');
        exitReact('home');
      }
    } catch (error) {
      console.error('Error navigating after dismiss:', error);
      history.push('loading');
      exitReact('home');
    }
  };

  const handleCollaborationChange = (value: string) => {
    setFeedback(prev => ({ ...prev, hygienistRating: value }));
  };

  const handleSubmit = (e?: any) => {
    if (e) {
      e.preventDefault();
    }

    if (saveFeedbackLoading || submitting) return;
    
    setSubmitting(true);

    const feedbackData = {
      jobId: job.id,
      hygienistId: hygienist.id,
      locationId: locationId,
      clinical: feedback.clinical,
      professional: feedback.professional,
      communication: feedback.communication,
      time: feedback.time,
      value: feedback.value,
      hygienistRating: feedback.hygienistRating,
      additionalFeedback: feedback.additionalFeedback,
      feedbackSubmitted: 1
    };

    submitFeedback({ data: feedbackData });
  };

  const [shouldRedirect, setShouldRedirect] = useState(false);

  const handleRedirect = () => {
    if (fromJobCompleted) {
      history.push('loading');
      exitReact('home');
      return;
    }

    if (surveyJobs && currentSurveyIndex !== undefined && currentSurveyIndex + 1 < surveyJobs.length) {
      const nextSurvey = surveyJobs[currentSurveyIndex + 1];
      history.replace('feedbackPage', {
        ...nextSurvey,
        surveyJobs,
        currentSurveyIndex: currentSurveyIndex + 1
      });

      setFeedback({
        clinical: { rating: null, comment: '' },
        professional: { rating: null, comment: '' },
        communication: { rating: null, comment: '' },
        time: { rating: null, comment: '' },
        value: { rating: null, comment: '' },
        hygienistRating: '',
        additionalFeedback: '',
        feedbackSubmitted: 0
      });
      setSelectedPositiveFeedback(`"Thanks so much, ${nextSurvey.hygienist.firstName}!"`);
      setNegativeFeedback("");
      setContactRequested(false);
      setContactName("");
    } else {
      history.push('loading');
      exitReact('home');
    }
  };

  const finalizeFeedback = () => {
    const postponedSurveys = JSON.parse(localStorage.getItem('postponeSurvey') || '[]');
    const updatedSurveys = postponedSurveys.filter((item: PostponeSurveyItem) =>
      item.jobId !== job.id
    );
    localStorage.setItem('postponeSurvey', JSON.stringify(updatedSurveys));
    setShouldRedirect(true);
  };

  const handlePositiveFeedbackSubmit = (positiveFeedback: string) => {
    setShowPositiveModal(false);
    if (positiveFeedback !== "not") {
      submitPositiveFeedback({
        job,
        feedback: positiveFeedback
      }).then(() => {
        setShowSuccessModal(true);
      }).catch((error: ApiError | Error) => {
        console.error('Error submitting positive feedback:', error);
        handleRedirect();
      });
    } else {
      handleRedirect();
    }
  };

  const handleNegativeFeedbackSubmit = () => {
    setShowNegativeModal(false);
    submitNegativeFeedback({
      job,
      negativeFeedback,
      contact: contactRequested,
      name: contactName
    }).then(() => {
      setShowNegativeSuccessModal(true);
    }).catch((error: ApiError | Error) => {
      console.error('Error submitting negative feedback:', error);
      handleRedirect();
    });
  };

  const handlePositiveSuccessModalClose = () => {
    setShowSuccessModal(false);
    if (shouldRedirect) {
      handleRedirect();
    }
  };

  const handleNegativeSuccessModalClose = () => {
    setShowNegativeSuccessModal(false);
    if (shouldRedirect) {
      handleRedirect();
    }
  };

  const updateRating = (category: RatingFieldKey) => (rating: number | null) => {
    setFeedback((prev) => {
      const currentValue = prev[category];
      return {
        ...prev,
        [category]: { ...currentValue, rating },
      };
    });
  };

  const updateComment = (category: RatingFieldKey) => (comment: string) => {
    setFeedback((prev) => {
      const currentValue = prev[category];
      return {
        ...prev,
        [category]: { ...currentValue, comment },
      };
    });
  };

  return (
    <PageContainer>
      <TopBarRedesigned
        onBack={handleBack}
        title={`Rate Your Experience with ${hygienist.firstName}`}
        isNoBackArrow={true}
      />
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ 
          textAlign: 'center',
          marginBottom: 1,
          fontSize: { xs: '0.9rem', sm: '1rem' }
        }}
      >
        Shift Date: {moment(job.startDate).format('MMMM D')}
      </Typography>

      <ContentContainer>
        <InfoBox>
          <Typography variant="body2" color="text.secondary">
            <span style={{ fontWeight: 'bold' }}>Your feedback is confidential</span> and will only be used internally by TempStars to improve
            matching quality. Honest, constructive feedback helps us ensure the best possible matches
            for dental offices and professionals. Your comments will not be displayed publicly.
          </Typography>
        </InfoBox>

        {ratingSections.map((section) => {
          // Ensure we're only accessing valid rating fields
          const type = section.type;
          const data = feedback[type];
          
          return (
            <RatingSection
              key={type}
              title={section.title}
              description={section.description}
              type={type}
              value={data.rating}
              comment={data.comment}
              onChange={updateRating(type)}
              onCommentChange={updateComment(type)}
            />
          );
        })}

        <FutureCollaboration
          value={feedback.hygienistRating}
          talentName={`${hygienist.firstName} ${hygienist.lastName}`}
          onChange={handleCollaborationChange}
        />

        <Box mb={8}>
          <Typography variant="h6" component="h3" gutterBottom>
            Additional Feedback (For TempStars internal use only)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Any other comments or observations about working with this professional..."
            value={feedback.additionalFeedback}
            onChange={(e) =>
              setFeedback((prev) => ({
                ...prev,
                additionalFeedback: e.target.value,
              }))
            }
          />
        </Box>
      </ContentContainer>

      <ButtonContainer>
        {!hasInput() ? (
          <StyledButton
            type="button"
            variant="contained"
            className="btn btn-primary"
            onClick={handleDismissWithoutFeedback}
            disabled={saveFeedbackLoading || positiveFeedbackLoading || negativeFeedbackLoading || submitting}
          >
            {submitting ? 'Dismissing...' : (saveFeedbackLoading ? 'Processing...' : 'Dismiss without feedback')}
          </StyledButton>
        ) : (
          <StyledButton
            type="submit"
            variant="contained"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saveFeedbackLoading || positiveFeedbackLoading || negativeFeedbackLoading || submitting}
          >
            {submitting ? 'Submitting...' : (saveFeedbackLoading ? 'Processing...' : 'Submit Feedback')}
          </StyledButton>
        )}
        <StyledButton
          type="button"
          variant="text"
          className="btn btn-text"
          onClick={handleBack}
          disabled={saveFeedbackLoading || positiveFeedbackLoading || negativeFeedbackLoading || submitting}
        >
          I didn't work that day, skip for now
        </StyledButton>
      </ButtonContainer>

      <PositiveFeedbackModal
        isOpen={showPositiveModal}
        onClose={() => setShowPositiveModal(false)}
        hygienistFirstName={hygienist.firstName}
        selectedFeedback={selectedPositiveFeedback}
        onFeedbackChange={setSelectedPositiveFeedback}
        onSubmit={handlePositiveFeedbackSubmit}
      />

      <PositiveSuccessModal
        isOpen={showSuccessModal}
        onClose={handlePositiveSuccessModalClose}
        hygienistFirstName={hygienist.firstName}
      />

      <NegativeFeedbackModal
        isOpen={showNegativeModal}
        onClose={() => setShowNegativeModal(false)}
        hygienistFirstName={hygienist.firstName}
        negativeFeedback={negativeFeedback}
        contactRequested={contactRequested}
        contactName={contactName}
        onFeedbackChange={setNegativeFeedback}
        onContactRequestedChange={setContactRequested}
        onContactNameChange={setContactName}
        onSubmit={handleNegativeFeedbackSubmit}
      />

      <NegativeSuccessModal
        isOpen={showNegativeSuccessModal}
        onClose={handleNegativeSuccessModalClose}
        hygienistFirstName={hygienist.firstName}
      />
      
      <FixedLoader loading={saveFeedbackLoading || positiveFeedbackLoading || negativeFeedbackLoading || submitting} />
      <FixedError error={saveFeedbackError || positiveFeedbackError || negativeFeedbackError || apiError} />
    </PageContainer>
  );
};

export default FeedbackPage;
